import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Plot from 'react-plotly.js';
import './ParetoAnalysis.css';

const ParetoAnalysis = () => {
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get('batchId');

  const [batchConfig, setBatchConfig] = useState(null);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [metricsData, setMetricsData] = useState(null);
  const [selectedSimulation, setSelectedSimulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [visualizationMode, setVisualizationMode] = useState('2d'); // '2d' or '3d'
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'optimal', 'roles', or 'selection'
  const [distanceThresholds, setDistanceThresholds] = useState({}); // {metric: percentage}
  const [nearbySimulations, setNearbySimulations] = useState([]);
  const [metricRanges, setMetricRanges] = useState({}); // {metric: {min, max}}
  const [assetRanges, setAssetRanges] = useState({}); // {asset: {min, max}}
  const [filteredSimulations, setFilteredSimulations] = useState([]); // Simulations matching selection criteria
  const [portfolioName, setPortfolioName] = useState('');
  const [creatingPortfolio, setCreatingPortfolio] = useState(false);
  const [portfolioCreated, setPortfolioCreated] = useState(null);
  const [percentileThreshold, setPercentileThreshold] = useState(5); // For role analysis, 0-50%
  const [roleSortConfig, setRoleSortConfig] = useState({ key: null, direction: 'asc' }); // Sorting for role analysis table
  const [pinnedPortfolios, setPinnedPortfolios] = useState([]); // Pinned portfolios (as composition strings)
  const [detectedPrefix, setDetectedPrefix] = useState(null); // Detected hdist prefix (e.g., 'p25', 'median')
  const [metricSuffixes, setMetricSuffixes] = useState([]); // Suffixes after prefix (e.g., 'price_cagr')
  const [metricType, setMetricType] = useState('standard'); // 'standard' or 'impact'

  // Available hdist prefixes
  const HDIST_PREFIXES = ['mean', 'median', 'std', 'min', 'max', 'p5', 'p25', 'p75', 'p95'];

  // Load batch analysis config on mount
  useEffect(() => {
    if (!batchId) {
      setError('Batch ID not provided');
      setLoading(false);
      return;
    }

    const fetchBatchConfig = async () => {
      try {
        const response = await api.get(`/simulation-batches/${batchId}/analysis-config`);
        setBatchConfig(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error loading batch config:', err);
        setError(err.response?.data?.detail || 'Failed to load batch configuration');
        setLoading(false);
      }
    };

    fetchBatchConfig();
  }, [batchId]);

  const handleMetricToggle = (metric) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metric)) {
        return prev.filter(m => m !== metric);
      } else {
        // Limit based on visualization mode: 2 for 2D, 3 for 3D
        const maxMetrics = visualizationMode === '3d' ? 3 : 2;
        if (prev.length >= maxMetrics) {
          return prev;
        }
        return [...prev, metric];
      }
    });
  };

  const handleVisualizationModeChange = (mode) => {
    setVisualizationMode(mode);
    // Reset metrics if switching modes and too many selected
    const maxMetrics = mode === '3d' ? 3 : 2;
    if (selectedMetrics.length > maxMetrics) {
      setSelectedMetrics(selectedMetrics.slice(0, maxMetrics));
    }
  };

  const loadMetricsData = async () => {
    const requiredMetrics = visualizationMode === '3d' ? 3 : 2;
    if (selectedMetrics.length !== requiredMetrics) {
      setError(`Please select exactly ${requiredMetrics} metrics for ${visualizationMode.toUpperCase()} analysis`);
      return;
    }

    setLoadingData(true);
    setError(null);

    try {
      const metricsParam = selectedMetrics.join(',');
      const response = await api.get(
        `/simulation-batches/${batchId}/metrics?metrics=${metricsParam}`
      );
      setMetricsData(response.data);

      // Initialize metric ranges with full range
      const initialMetricRanges = {};
      selectedMetrics.forEach(metric => {
        const values = response.data.data.map(d => d[metric]);
        initialMetricRanges[metric] = {
          min: Math.min(...values),
          max: Math.max(...values)
        };
      });
      setMetricRanges(initialMetricRanges);

      // Initialize asset ranges with full range (0-100%)
      const allAssets = new Set();
      response.data.data.forEach(sim => {
        const composition = Object.entries(sim.allocation || {}).map(([symbol, fraction]) => ({
          symbol,
          percentage: fraction * 100
        }));
        composition.forEach(asset => allAssets.add(asset.symbol));
      });

      const initialAssetRanges = {};
      allAssets.forEach(asset => {
        initialAssetRanges[asset] = { min: 0, max: 100 };
      });
      setAssetRanges(initialAssetRanges);

    } catch (err) {
      console.error('Error loading metrics data:', err);
      setError(err.response?.data?.detail || 'Failed to load metrics data');
    } finally {
      setLoadingData(false);
    }
  };

  const getPortfolioComposition = useCallback((simulation) => {
    // Check if there's an override in type_params
    if (simulation.type_params?.override_assets) {
      return simulation.type_params.override_assets;
    }

    // New format: allocation as dict {"XLC": 0.084, "XLK": 0.058}
    if (simulation.allocation) {
      return Object.entries(simulation.allocation).map(([symbol, fraction]) => ({
        symbol,
        percentage: fraction * 100, // Return as number for calculations
        amount: null // Amount not available in batched format
      }));
    }

    // Legacy format: portfolio_assets as array of objects
    if (simulation.portfolio_assets) {
      return simulation.portfolio_assets;
    }

    return [];
  }, []);

  // Get unique portfolio ID based on composition
  const getPortfolioId = useCallback((simulation) => {
    const composition = getPortfolioComposition(simulation);
    return JSON.stringify(composition.map(a => [a.symbol, a.percentage]));
  }, [getPortfolioComposition]);

  // Check if portfolio is pinned
  const isPinned = useCallback((simulation) => {
    const portfolioId = getPortfolioId(simulation);
    return pinnedPortfolios.includes(portfolioId);
  }, [pinnedPortfolios, getPortfolioId]);

  // Toggle pin for filtered portfolios
  const togglePinGroup = useCallback(() => {
    if (pinnedPortfolios.length > 0) {
      // Unpin all
      setPinnedPortfolios([]);
    } else {
      // Pin current filtered portfolios
      const ids = filteredSimulations.map(sim => getPortfolioId(sim));
      setPinnedPortfolios(ids);
    }
  }, [pinnedPortfolios, filteredSimulations, getPortfolioId]);

  const formatMetricName = (metric) => {
    return metric.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Detect if all selected metrics have the same hdist prefix
  const detectHdistPrefix = useCallback((metrics) => {
    console.log('[detectHdistPrefix] Входные метрики:', metrics);

    if (metrics.length === 0) {
      console.log('[detectHdistPrefix] Метрики не выбраны, выход');
      return null;
    }

    // Use known prefixes in regex to avoid greedy matching
    // Pattern now captures optional _impact suffix: hdist_<prefix>_<suffix>(_impact)?
    const prefixPattern = /^hdist_(mean|median|std|min|max|p5|p25|p75|p95)_(.+?)(_impact)?$/;
    const parsedMetrics = metrics.map(m => {
      const match = m.match(prefixPattern);
      return match ? { prefix: match[1], suffix: match[2], isImpact: !!match[3] } : null;
    });

    console.log('[detectHdistPrefix] Распарсенные метрики:', parsedMetrics);

    // Check if all metrics match the pattern
    if (parsedMetrics.some(p => p === null)) {
      console.log('[detectHdistPrefix] ❌ Не все метрики соответствуют паттерну hdist_<prefix>_<suffix>');
      return null;
    }

    // Check if all have the same prefix
    const firstPrefix = parsedMetrics[0].prefix;
    if (!parsedMetrics.every(p => p.prefix === firstPrefix)) {
      console.log('[detectHdistPrefix] ❌ Метрики имеют разные префиксы:',
        parsedMetrics.map(p => p.prefix));
      return null;
    }

    // Check if all have the same type (standard or impact)
    const firstIsImpact = parsedMetrics[0].isImpact;
    if (!parsedMetrics.every(p => p.isImpact === firstIsImpact)) {
      console.log('[detectHdistPrefix] ❌ Метрики имеют разные типы (standard/impact)');
      return null;
    }

    const result = {
      prefix: firstPrefix,
      suffixes: parsedMetrics.map(p => p.suffix),
      isImpact: firstIsImpact
    };

    console.log('[detectHdistPrefix] ✅ УСПЕШНО детектирован префикс:', result);

    return result;
  }, []);

  // Switch between standard and impact metrics
  const switchMetricType = useCallback(async (newType) => {
    if (!metricSuffixes || metricSuffixes.length === 0 || !detectedPrefix) return;

    // Save current selections before switching
    const savedFiltered = filteredSimulations.map(sim =>
      JSON.stringify(getPortfolioComposition(sim).map(a => [a.symbol, a.percentage]))
    );
    const savedSelected = selectedSimulation ?
      JSON.stringify(getPortfolioComposition(selectedSimulation).map(a => [a.symbol, a.percentage]))
      : null;

    // Build new metrics with the new type
    const impactSuffix = newType === 'impact' ? '_impact' : '';
    const newMetrics = metricSuffixes.map(suffix => `hdist_${detectedPrefix}_${suffix}${impactSuffix}`);

    // Update selected metrics
    setSelectedMetrics(newMetrics);

    // Reload data with new metrics
    setLoadingData(true);
    setError(null);

    try {
      const metricsParam = newMetrics.join(',');
      const response = await api.get(
        `/simulation-batches/${batchId}/metrics?metrics=${metricsParam}`
      );
      setMetricsData(response.data);

      // Update metric type
      setMetricType(newType);

      // Re-initialize metric ranges with new data
      const initialMetricRanges = {};
      newMetrics.forEach(metric => {
        const values = response.data.data.map(d => d[metric]);
        initialMetricRanges[metric] = {
          min: Math.min(...values),
          max: Math.max(...values)
        };
      });
      setMetricRanges(initialMetricRanges);

      // Restore filtered portfolios by matching composition
      if (savedFiltered.length > 0) {
        const restoredFiltered = response.data.data.filter(sim => {
          const composition = JSON.stringify(
            getPortfolioComposition(sim).map(a => [a.symbol, a.percentage])
          );
          return savedFiltered.includes(composition);
        });
        setFilteredSimulations(restoredFiltered);
      }

      // Restore selected simulation by matching composition
      if (savedSelected) {
        const restoredSelected = response.data.data.find(sim => {
          const composition = JSON.stringify(
            getPortfolioComposition(sim).map(a => [a.symbol, a.percentage])
          );
          return composition === savedSelected;
        });
        if (restoredSelected) {
          setSelectedSimulation(restoredSelected);
        }
      }

    } catch (err) {
      console.error('Error switching metric type:', err);
      setError(err.response?.data?.detail || 'Failed to load metrics with new type');
    } finally {
      setLoadingData(false);
    }
  }, [metricSuffixes, detectedPrefix, batchId, filteredSimulations, selectedSimulation, getPortfolioComposition]);

  // Switch to a different hdist prefix
  const switchHdistPrefix = useCallback(async (newPrefix) => {
    if (!metricSuffixes || metricSuffixes.length === 0) return;

    // Save current selections before switching
    const savedFiltered = filteredSimulations.map(sim =>
      JSON.stringify(getPortfolioComposition(sim).map(a => [a.symbol, a.percentage]))
    );
    const savedSelected = selectedSimulation ?
      JSON.stringify(getPortfolioComposition(selectedSimulation).map(a => [a.symbol, a.percentage]))
      : null;

    // Build new metrics with the new prefix (preserve impact suffix)
    const impactSuffix = metricType === 'impact' ? '_impact' : '';
    const newMetrics = metricSuffixes.map(suffix => `hdist_${newPrefix}_${suffix}${impactSuffix}`);

    // Update selected metrics
    setSelectedMetrics(newMetrics);

    // Reload data with new metrics
    setLoadingData(true);
    setError(null);

    try {
      const metricsParam = newMetrics.join(',');
      const response = await api.get(
        `/simulation-batches/${batchId}/metrics?metrics=${metricsParam}`
      );
      setMetricsData(response.data);

      // Update detected prefix
      setDetectedPrefix(newPrefix);

      // Re-initialize metric ranges with new data
      const initialMetricRanges = {};
      newMetrics.forEach(metric => {
        const values = response.data.data.map(d => d[metric]);
        initialMetricRanges[metric] = {
          min: Math.min(...values),
          max: Math.max(...values)
        };
      });
      setMetricRanges(initialMetricRanges);

      // Restore filtered portfolios by matching composition
      if (savedFiltered.length > 0) {
        const restoredFiltered = response.data.data.filter(sim => {
          const composition = JSON.stringify(
            getPortfolioComposition(sim).map(a => [a.symbol, a.percentage])
          );
          return savedFiltered.includes(composition);
        });
        setFilteredSimulations(restoredFiltered);
      }

      // Restore selected simulation
      if (savedSelected) {
        const restoredSelected = response.data.data.find(sim => {
          const composition = JSON.stringify(
            getPortfolioComposition(sim).map(a => [a.symbol, a.percentage])
          );
          return composition === savedSelected;
        });
        if (restoredSelected) {
          setSelectedSimulation(restoredSelected);
        }
      }

    } catch (err) {
      console.error('Error loading metrics data:', err);
      setError(err.response?.data?.detail || 'Failed to load metrics data');
    } finally {
      setLoadingData(false);
    }
  }, [batchId, metricSuffixes, filteredSimulations, selectedSimulation, getPortfolioComposition]);

  // Detect prefix when metrics are selected
  useEffect(() => {
    const detected = detectHdistPrefix(selectedMetrics);
    if (detected) {
      setDetectedPrefix(detected.prefix);
      setMetricSuffixes(detected.suffixes);
      setMetricType(detected.isImpact ? 'impact' : 'standard');
    } else {
      setDetectedPrefix(null);
      setMetricSuffixes([]);
    }
  }, [selectedMetrics, detectHdistPrefix]);

  const handleSimulationClick = useCallback((simulation) => {
    setSelectedSimulation(simulation);
    // Don't reset tab - let user stay on current tab

    // Initialize distance thresholds if not set
    if (Object.keys(distanceThresholds).length === 0 && selectedMetrics.length > 0) {
      const initialThresholds = {};
      selectedMetrics.forEach(metric => {
        const values = metricsData.data.map(d => d[metric]);
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        const range = maxValue - minValue;
        initialThresholds[metric] = range * 0.05; // 5% of range
      });
      setDistanceThresholds(initialThresholds);
    }

    // Calculate nearby simulations
    calculateNearbySimulations(simulation);
  }, [selectedMetrics, metricsData, distanceThresholds]);

  const calculateNearbySimulations = useCallback((centerSimulation) => {
    if (!metricsData?.data || !centerSimulation) {
      setNearbySimulations([]);
      return;
    }

    const nearby = metricsData.data.filter(sim => {
      if (sim === centerSimulation) return true; // Include the center point itself

      // Check if simulation is within threshold for ALL metrics (component-wise distance)
      return selectedMetrics.every(metric => {
        const distance = Math.abs(sim[metric] - centerSimulation[metric]);
        return distance <= (distanceThresholds[metric] || 0);
      });
    });

    setNearbySimulations(nearby);
  }, [metricsData, selectedMetrics, distanceThresholds]);

  const calculateSyntheticPortfolio = useCallback(() => {
    if (nearbySimulations.length === 0) return null;

    // Get all portfolio compositions
    const portfolios = nearbySimulations.map(sim => getPortfolioComposition(sim));

    // Aggregate assets across all portfolios
    const assetMap = {};

    portfolios.forEach(portfolio => {
      portfolio.forEach(asset => {
        if (!assetMap[asset.symbol]) {
          assetMap[asset.symbol] = {
            symbol: asset.symbol,
            totalPercentage: 0,
            totalAmount: 0,
            count: 0
          };
        }
        assetMap[asset.symbol].totalPercentage += asset.percentage || 0;
        assetMap[asset.symbol].totalAmount += asset.amount || 0;
        assetMap[asset.symbol].count += 1;
      });
    });

    // Calculate average for each asset
    const syntheticPortfolio = Object.values(assetMap).map(asset => ({
      symbol: asset.symbol,
      percentage: (asset.totalPercentage / nearbySimulations.length).toFixed(2),
      amount: Math.round(asset.totalAmount / nearbySimulations.length)
    }));

    // Sort by percentage descending
    return syntheticPortfolio.sort((a, b) => b.percentage - a.percentage);
  }, [nearbySimulations, getPortfolioComposition]);

  // Calculate blended portfolio from pinned portfolios
  const calculateBlendedPinnedPortfolio = useCallback(() => {
    if (pinnedPortfolios.length === 0 || !metricsData?.data) return null;

    // Get all pinned simulations
    const pinnedSims = metricsData.data.filter(sim => isPinned(sim));

    if (pinnedSims.length === 0) return null;

    // Aggregate asset weights
    const assetMap = {};

    pinnedSims.forEach(sim => {
      const composition = getPortfolioComposition(sim);
      composition.forEach(asset => {
        if (!assetMap[asset.symbol]) {
          assetMap[asset.symbol] = { totalWeight: 0, count: 0 };
        }
        assetMap[asset.symbol].totalWeight += asset.percentage || 0;
        assetMap[asset.symbol].count += 1;
      });
    });

    // Calculate average weight for each asset (as percentage 0-100)
    const blendedWeights = {};
    Object.keys(assetMap).forEach(symbol => {
      blendedWeights[symbol] = assetMap[symbol].totalWeight / pinnedSims.length;
    });

    return blendedWeights;
  }, [pinnedPortfolios, metricsData, isPinned, getPortfolioComposition]);

  // Calculate Role Polarity Score for each asset across all portfolios
  const calculateRoleAnalysis = useCallback(() => {
    if (!metricsData?.data || selectedMetrics.length === 0) return null;

    const allSimulations = metricsData.data;
    const threshold = percentileThreshold / 100; // Convert to 0-0.5

    // Get all unique assets
    const allAssets = new Set();
    allSimulations.forEach(sim => {
      const composition = getPortfolioComposition(sim);
      composition.forEach(asset => allAssets.add(asset.symbol));
    });

    // Calculate role score for each metric
    const roleScores = {};

    selectedMetrics.forEach(metric => {
      // Sort simulations by this metric (descending - higher is better)
      const sortedSims = [...allSimulations].sort((a, b) => b[metric] - a[metric]);

      // Get top N% and worst N%
      const topCount = Math.max(1, Math.floor(sortedSims.length * threshold));
      const topSims = sortedSims.slice(0, topCount);
      const worstSims = sortedSims.slice(-topCount);

      // Calculate average weights for top and worst groups
      const topWeights = {};
      const worstWeights = {};

      allAssets.forEach(assetSymbol => {
        topWeights[assetSymbol] = 0;
        worstWeights[assetSymbol] = 0;
      });

      topSims.forEach(sim => {
        const composition = getPortfolioComposition(sim);
        composition.forEach(asset => {
          topWeights[asset.symbol] += (asset.percentage || 0) / topSims.length;
        });
      });

      worstSims.forEach(sim => {
        const composition = getPortfolioComposition(sim);
        composition.forEach(asset => {
          worstWeights[asset.symbol] += (asset.percentage || 0) / worstSims.length;
        });
      });

      // Calculate role score: weight(top) - weight(worst), range -100 to +100
      allAssets.forEach(assetSymbol => {
        if (!roleScores[assetSymbol]) {
          roleScores[assetSymbol] = {};
        }
        roleScores[assetSymbol][metric] = topWeights[assetSymbol] - worstWeights[assetSymbol];
      });
    });

    return roleScores;
  }, [metricsData, selectedMetrics, percentileThreshold, getPortfolioComposition]);

  // Get color for role score (-100 to +100) using color scale
  const getRoleScoreColor = (score) => {
    // Normalize to 0-1 range where 0.5 is yellow (score=0)
    const normalized = (score + 100) / 200;

    if (normalized < 0.5) {
      // Red to Yellow: score from -100 to 0
      const r = 255;
      const g = Math.round(255 * (normalized * 2));
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // Yellow to Green: score from 0 to 100
      const r = Math.round(255 * (1 - (normalized - 0.5) * 2));
      const g = 255;
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
    }
  };

  // Filter simulations by metric and asset ranges
  const calculateFilteredSimulations = useCallback(() => {
    if (!metricsData?.data) {
      setFilteredSimulations([]);
      return;
    }

    const filtered = metricsData.data.filter(sim => {
      // Check metric ranges
      for (const metric of selectedMetrics) {
        if (metricRanges[metric]) {
          const value = sim[metric];
          if (value < metricRanges[metric].min || value > metricRanges[metric].max) {
            return false;
          }
        }
      }

      // Check asset ranges
      const composition = getPortfolioComposition(sim);
      for (const [assetSymbol, range] of Object.entries(assetRanges)) {
        const asset = composition.find(a => a.symbol === assetSymbol);
        const percentage = asset ? asset.percentage : 0;

        if (percentage < range.min || percentage > range.max) {
          return false;
        }
      }

      return true;
    });

    setFilteredSimulations(filtered);
  }, [metricsData, selectedMetrics, metricRanges, assetRanges, getPortfolioComposition]);

  // Recalculate filtered simulations when ranges change
  useEffect(() => {
    if (activeTab === 'selection') {
      calculateFilteredSimulations();
    }
  }, [activeTab, metricRanges, assetRanges, calculateFilteredSimulations]);

  const handleThresholdChange = useCallback((metric, value) => {
    setDistanceThresholds(prev => ({
      ...prev,
      [metric]: value
    }));

    // Recalculate nearby simulations with new threshold
    if (selectedSimulation) {
      setTimeout(() => calculateNearbySimulations(selectedSimulation), 0);
    }
  }, [selectedSimulation, calculateNearbySimulations]);

  const generateDefaultPortfolioName = useCallback(() => {
    if (!selectedSimulation || !batchConfig) return '';

    const batchName = batchConfig.batch_name || 'Batch';
    const simNumber = metricsData?.data?.indexOf(selectedSimulation) + 1 || 1;
    const timestamp = new Date().toISOString().split('T')[0];

    return `${batchName}_Synthetic_Sim${simNumber}_${timestamp}`;
  }, [selectedSimulation, batchConfig, metricsData]);

  const handleCreatePortfolio = useCallback(async () => {
    const syntheticPortfolio = calculateSyntheticPortfolio();
    if (!syntheticPortfolio || syntheticPortfolio.length === 0) {
      alert('No synthetic portfolio to create');
      return;
    }

    setCreatingPortfolio(true);
    setPortfolioCreated(null);

    try {
      const portfolioData = {
        name: portfolioName || generateDefaultPortfolioName(),
        description: `Synthetic portfolio from ${nearbySimulations.length} nearby simulations (batch: ${batchConfig?.batch_name})`,
        total_amount: 100000, // Default amount
        currency: 'USD',
        assets: syntheticPortfolio.map(asset => ({
          symbol: asset.symbol,
          percentage: parseFloat(asset.percentage),
          name: asset.symbol // Can be enhanced with full names
        }))
      };

      const response = await api.post('/portfolios', portfolioData);

      setPortfolioCreated({
        success: true,
        portfolioId: response.data.id,
        portfolioName: response.data.name
      });

      // Reset name field
      setPortfolioName('');

    } catch (err) {
      console.error('Error creating portfolio:', err);
      setPortfolioCreated({
        success: false,
        error: err.response?.data?.detail || 'Failed to create portfolio'
      });
    } finally {
      setCreatingPortfolio(false);
    }
  }, [portfolioName, generateDefaultPortfolioName, calculateSyntheticPortfolio, nearbySimulations, batchConfig]);

  // Update portfolio name when simulation changes
  useEffect(() => {
    if (selectedSimulation && activeTab === 'optimal') {
      setPortfolioName(generateDefaultPortfolioName());
    }
  }, [selectedSimulation, activeTab, generateDefaultPortfolioName]);

  // Memoized 3D Plot to prevent camera reset on state changes
  const plot3D = useMemo(() => {
    if (!metricsData?.data || visualizationMode !== '3d') return null;

    // Determine colors for each point based on pinned/filtered/nearby simulations
    const pointColors = metricsData.data.map(d => {
      // Priority: pinned (red) > filtered (purple) > nearby (orange) > default (light blue)
      if (isPinned(d)) {
        return '#E74C3C'; // Red for pinned portfolios
      }

      if (activeTab === 'selection' && filteredSimulations.length > 0) {
        const isFiltered = filteredSimulations.some(filtered => filtered === d);
        return isFiltered ? '#9B59B6' : '#D3D3D3'; // Purple for filtered, light gray for others
      }

      if (selectedSimulation && nearbySimulations.length > 0) {
        const isNearby = nearbySimulations.some(nearby => nearby === d);
        return isNearby ? '#FFA500' : '#87CEEB'; // Orange for nearby, light blue for others
      }

      return '#87CEEB'; // Default light blue
    });

    return (
      <Plot
        data={[
          {
            type: 'scatter3d',
            mode: 'markers',
            x: metricsData.data.map(d => d[selectedMetrics[0]]),
            y: metricsData.data.map(d => d[selectedMetrics[1]]),
            z: metricsData.data.map(d => d[selectedMetrics[2]]),
            marker: {
              size: 6,
              color: pointColors,
              line: {
                color: 'rgba(74, 144, 226, 0.8)',
                width: 0.5
              },
              opacity: 0.8
            },
            text: metricsData.data.map(d => {
              const assets = getPortfolioComposition(d);
              return `${formatMetricName(selectedMetrics[0])}: ${d[selectedMetrics[0]]?.toFixed(2)}<br>` +
                     `${formatMetricName(selectedMetrics[1])}: ${d[selectedMetrics[1]]?.toFixed(2)}<br>` +
                     `${formatMetricName(selectedMetrics[2])}: ${d[selectedMetrics[2]]?.toFixed(2)}<br>` +
                     `<br><b>Portfolio:</b><br>` +
                     assets.map(a => `${a.symbol}: ${(a.percentage || 0).toFixed(2)}%`).join('<br>') +
                     `<br><br><i>Click to view details</i>`;
            }),
            hoverinfo: 'text',
            hoverlabel: {
              bgcolor: 'white',
              bordercolor: '#4A90E2',
              font: { size: 12, color: '#333' }
            }
          }
        ]}
        layout={{
          width: 900,
          height: 700,
          uirevision: 'constant', // This preserves camera position across updates
          scene: {
            xaxis: {
              title: formatMetricName(selectedMetrics[0]),
              gridcolor: '#e0e0e0',
              showbackground: true,
              backgroundcolor: 'rgba(240, 240, 240, 0.5)'
            },
            yaxis: {
              title: formatMetricName(selectedMetrics[1]),
              gridcolor: '#e0e0e0',
              showbackground: true,
              backgroundcolor: 'rgba(240, 240, 240, 0.5)'
            },
            zaxis: {
              title: formatMetricName(selectedMetrics[2]),
              gridcolor: '#e0e0e0',
              showbackground: true,
              backgroundcolor: 'rgba(240, 240, 240, 0.5)'
            },
            camera: {
              eye: { x: 1.5, y: 1.5, z: 1.5 }
            }
          },
          margin: { l: 0, r: 0, b: 0, t: 30 },
          paper_bgcolor: 'rgba(0,0,0,0)',
          plot_bgcolor: 'rgba(0,0,0,0)',
          hovermode: 'closest',
          clickmode: 'event+select'
        }}
        config={{
          displayModeBar: true,
          displaylogo: false,
          modeBarButtonsToRemove: ['pan2d', 'lasso2d'],
          responsive: true
        }}
        useResizeHandler={true}
        style={{ width: '100%', height: '100%' }}
        onClick={(event) => {
          if (event && event.points && event.points.length > 0) {
            const point = event.points[0];
            const pointIndex = point.pointNumber ?? point.pointIndex ?? point.i;

            if (pointIndex !== undefined && metricsData.data[pointIndex]) {
              handleSimulationClick(metricsData.data[pointIndex]);
            }
          }
        }}
      />
    );
  }, [metricsData, selectedMetrics, visualizationMode, getPortfolioComposition, handleSimulationClick, selectedSimulation, nearbySimulations, activeTab, filteredSimulations, isPinned]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    const assets = getPortfolioComposition(data);

    return (
      <div className="custom-tooltip">
        <h4>Simulation Details</h4>
        <div className="tooltip-metrics">
          {selectedMetrics.map((metric, idx) => (
            <p key={metric}>
              <strong>{formatMetricName(metric)}:</strong> {data[metric]?.toFixed(2)}
            </p>
          ))}
        </div>
        <div className="tooltip-portfolio">
          <strong>Portfolio Composition:</strong>
          <ul>
            {assets.map((asset, idx) => (
              <li key={idx}>
                {asset.symbol}: {(asset.percentage || 0).toFixed(2)}% (${asset.amount?.toLocaleString()})
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pareto-analysis">
        <h1>Pareto Analysis</h1>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pareto-analysis">
        <h1>Pareto Analysis</h1>
        <div className="error-message">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pareto-analysis">
      <h1>Pareto Analysis</h1>

      {batchConfig && (
        <>
          <div className="batch-info">
            <h2>{batchConfig.batch_name}</h2>
            <p><strong>Simulation Type:</strong> {batchConfig.simulation_type.name}</p>
          </div>

          <div className="visualization-mode-toggle">
            <h3>Visualization Mode</h3>
            <div className="mode-buttons">
              <button
                className={visualizationMode === '2d' ? 'active' : ''}
                onClick={() => handleVisualizationModeChange('2d')}
              >
                2D Pareto Front
              </button>
              <button
                className={visualizationMode === '3d' ? 'active' : ''}
                onClick={() => handleVisualizationModeChange('3d')}
              >
                3D Scatter Plot
              </button>
            </div>
          </div>

          <div className="metrics-selection">
            <h3>Select Metrics ({visualizationMode === '2d' ? '2 for X/Y axes' : '3 for X/Y/Z axes'})</h3>
            <p className="metrics-hint">
              {visualizationMode === '2d'
                ? 'Choose 2 metrics: one for X-axis, one for Y-axis'
                : 'Choose 3 metrics: one for X-axis, one for Y-axis, one for Z-axis'}
            </p>

            <div className="metrics-grid">
              {batchConfig.simulation_type.available_metrics.map(metric => (
                <div key={metric} className="metric-checkbox">
                  <input
                    type="checkbox"
                    id={metric}
                    checked={selectedMetrics.includes(metric)}
                    onChange={() => handleMetricToggle(metric)}
                    disabled={!selectedMetrics.includes(metric) && selectedMetrics.length >= (visualizationMode === '3d' ? 3 : 2)}
                  />
                  <label htmlFor={metric}>
                    {formatMetricName(metric)}
                  </label>
                </div>
              ))}
            </div>

            <div className="selected-metrics-summary">
              <strong>Selected metrics ({selectedMetrics.length}/{visualizationMode === '3d' ? 3 : 2}):</strong>
              {selectedMetrics.length > 0 ? (
                <ul>
                  {selectedMetrics.map((metric, idx) => {
                    let axisLabel = 'X-axis';
                    if (idx === 1) axisLabel = 'Y-axis';
                    if (idx === 2) axisLabel = 'Z-axis';
                    return (
                      <li key={metric}>
                        <strong>{axisLabel}:</strong> {formatMetricName(metric)}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>No metrics selected yet</p>
              )}
            </div>

            <div className="load-data-section">
              <button
                onClick={loadMetricsData}
                disabled={selectedMetrics.length !== (visualizationMode === '3d' ? 3 : 2) || loadingData}
                className="load-data-btn"
              >
                {loadingData ? 'Loading...' : `Build ${visualizationMode === '3d' ? '3D' : 'Pareto'} Visualization`}
              </button>
              {selectedMetrics.length !== (visualizationMode === '3d' ? 3 : 2) && (
                <p className="hint-text">
                  Select exactly {visualizationMode === '3d' ? 3 : 2} metrics to build visualization
                </p>
              )}
            </div>
          </div>

          {metricsData && (
            <div className="pareto-visualization">
              <div className="chart-section">
                <div className="chart-header">
                  <h3>{visualizationMode === '2d' ? 'Pareto Front Analysis' : '3D Scatter Plot Analysis'}</h3>

                  {/* Hdist prefix switcher */}
                  {detectedPrefix && (
                    <>
                      <div className="hdist-prefix-switcher">
                        <label htmlFor="prefix-select">Distribution Scenario:</label>
                        <select
                          id="prefix-select"
                          value={detectedPrefix}
                          onChange={(e) => switchHdistPrefix(e.target.value)}
                          disabled={loadingData}
                          className="prefix-dropdown"
                        >
                          {HDIST_PREFIXES.map(prefix => (
                            <option key={prefix} value={prefix}>
                              {prefix.toUpperCase()}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="hdist-prefix-switcher">
                        <label htmlFor="type-select">Metric Type:</label>
                        <select
                          id="type-select"
                          value={metricType}
                          onChange={(e) => switchMetricType(e.target.value)}
                          disabled={loadingData}
                          className="prefix-dropdown"
                        >
                          <option value="standard">Standard</option>
                          <option value="impact">Impact</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>

                <p className="chart-info">
                  <strong>Total simulations:</strong> {metricsData.data.length} |
                  <strong> X-axis:</strong> {formatMetricName(selectedMetrics[0])} |
                  <strong> Y-axis:</strong> {formatMetricName(selectedMetrics[1])}
                  {visualizationMode === '3d' && selectedMetrics[2] && (
                    <> | <strong> Z-axis:</strong> {formatMetricName(selectedMetrics[2])}</>
                  )}
                </p>

                {visualizationMode === '2d' ? (
                  <ResponsiveContainer width="100%" height={500}>
                    <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey={selectedMetrics[0]}
                        type="number"
                        name={formatMetricName(selectedMetrics[0])}
                        label={{ value: formatMetricName(selectedMetrics[0]), position: 'insideBottom', offset: -10 }}
                      />
                      <YAxis
                        dataKey={selectedMetrics[1]}
                        type="number"
                        name={formatMetricName(selectedMetrics[1])}
                        label={{ value: formatMetricName(selectedMetrics[1]), angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      {/* Default points - light blue/transparent */}
                      <Scatter
                        name="All Simulations"
                        data={metricsData.data}
                        fill="#87CEEB"
                        opacity={0.4}
                        onClick={(data) => handleSimulationClick(data)}
                        cursor="pointer"
                      />
                      {/* Filtered portfolios - purple */}
                      {activeTab === 'selection' && filteredSimulations.length > 0 && (
                        <Scatter
                          name="Filtered Portfolios"
                          data={filteredSimulations}
                          fill="#9B59B6"
                          opacity={0.7}
                          onClick={(data) => handleSimulationClick(data)}
                          cursor="pointer"
                        />
                      )}
                      {/* Nearby points - orange */}
                      {activeTab !== 'selection' && selectedSimulation && nearbySimulations.length > 0 && (
                        <Scatter
                          name="Nearby Simulations"
                          data={nearbySimulations}
                          fill="#FFA500"
                          opacity={0.6}
                          onClick={(data) => handleSimulationClick(data)}
                          cursor="pointer"
                        />
                      )}
                      {/* Pinned portfolios - red (highest priority) */}
                      {pinnedPortfolios.length > 0 && (
                        <Scatter
                          name="Pinned Portfolios"
                          data={metricsData.data.filter(sim => isPinned(sim))}
                          fill="#E74C3C"
                          opacity={0.9}
                          onClick={(data) => handleSimulationClick(data)}
                          cursor="pointer"
                        />
                      )}
                      {/* Selected point - blue */}
                      {selectedSimulation && (
                        <Scatter
                          name="Selected Simulation"
                          data={[selectedSimulation]}
                          fill="#4A90E2"
                          opacity={1}
                          shape="star"
                        />
                      )}
                    </ScatterChart>
                  </ResponsiveContainer>
                ) : plot3D}
              </div>

              {selectedSimulation && (
                <div className="simulation-details-panel">
                  <div className="panel-header">
                    <h3>Simulation Info</h3>
                    <button
                      className="close-btn"
                      onClick={() => setSelectedSimulation(null)}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="panel-tabs">
                    <button
                      className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                      onClick={() => setActiveTab('details')}
                    >
                      Simulation Details
                    </button>
                    <button
                      className={`tab-btn ${activeTab === 'optimal' ? 'active' : ''}`}
                      onClick={() => setActiveTab('optimal')}
                    >
                      Synthetic Portfolio
                    </button>
                    <button
                      className={`tab-btn ${activeTab === 'roles' ? 'active' : ''}`}
                      onClick={() => setActiveTab('roles')}
                    >
                      Asset Roles
                    </button>
                    <button
                      className={`tab-btn ${activeTab === 'selection' ? 'active' : ''}`}
                      onClick={() => setActiveTab('selection')}
                    >
                      Portfolio Selection
                    </button>
                  </div>

                  <div className="panel-content">
                    {activeTab === 'details' ? (
                      <>
                        <div className="detail-section">
                          <h4>Metrics</h4>
                          {selectedMetrics.map(metric => (
                            <p key={metric}>
                              <strong>{formatMetricName(metric)}:</strong> {selectedSimulation[metric]?.toFixed(4)}
                            </p>
                          ))}
                        </div>

                        <div className="detail-section">
                          <h4>Portfolio Composition</h4>
                          <div className="assets-list">
                            {getPortfolioComposition(selectedSimulation).map((asset, idx) => (
                              <div key={idx} className="asset-item">
                                <span className="asset-symbol">{asset.symbol}</span>
                                <span className="asset-percentage">{(asset.percentage || 0).toFixed(2)}%</span>
                                <span className="asset-amount">${asset.amount?.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="detail-section">
                          <h4>Simulation Parameters</h4>
                          <pre className="params-json">
                            {JSON.stringify(selectedSimulation.type_params, null, 2)}
                          </pre>
                        </div>
                      </>
                    ) : activeTab === 'optimal' ? (
                      <div className="optimal-portfolio-content">
                        {selectedSimulation && metricsData ? (
                          <>
                            <div className="distance-controls">
                              <h4>Distance Thresholds</h4>
                              <p className="control-hint">
                                Adjust thresholds to define nearby simulations ({nearbySimulations.length} found)
                              </p>
                              {selectedMetrics.slice(0, visualizationMode === '3d' ? 3 : 2).map(metric => {
                                const values = metricsData.data.map(d => d[metric]);
                                const minValue = Math.min(...values);
                                const maxValue = Math.max(...values);
                                const range = maxValue - minValue;
                                const currentThreshold = distanceThresholds[metric] || (range * 0.05);
                                const percentage = ((currentThreshold / range) * 100).toFixed(1);

                                return (
                                  <div key={metric} className="threshold-control">
                                    <label>{formatMetricName(metric)}</label>
                                    <div className="slider-container">
                                      <input
                                        type="range"
                                        min="0"
                                        max={range / 2}
                                        step={range / 200}
                                        value={currentThreshold}
                                        onChange={(e) => handleThresholdChange(metric, parseFloat(e.target.value))}
                                        className="threshold-slider"
                                      />
                                      <span className="threshold-value">
                                        ±{currentThreshold.toFixed(4)} ({percentage}%)
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            <div className="synthetic-portfolio">
                              <h4>Synthetic Portfolio</h4>
                              <p className="portfolio-hint">
                                Average composition from {nearbySimulations.length} nearby simulation(s)
                              </p>
                              {calculateSyntheticPortfolio() ? (
                                <>
                                  <div className="synthetic-assets-list">
                                    {calculateSyntheticPortfolio().map((asset, idx) => (
                                      <div key={idx} className="synthetic-asset-item">
                                        <span className="asset-symbol">{asset.symbol}</span>
                                        <div className="asset-stats">
                                          <span className="asset-percentage">{(parseFloat(asset.percentage) || 0).toFixed(2)}%</span>
                                          <span className="asset-amount">${asset.amount?.toLocaleString()}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  <div className="create-portfolio-section">
                                    <h4>Create Portfolio</h4>
                                    <div className="portfolio-name-input">
                                      <label htmlFor="portfolio-name">Portfolio Name:</label>
                                      <input
                                        type="text"
                                        id="portfolio-name"
                                        value={portfolioName}
                                        onChange={(e) => setPortfolioName(e.target.value)}
                                        placeholder={generateDefaultPortfolioName()}
                                        disabled={creatingPortfolio}
                                      />
                                    </div>
                                    <button
                                      className="create-portfolio-btn"
                                      onClick={handleCreatePortfolio}
                                      disabled={creatingPortfolio || nearbySimulations.length === 0}
                                    >
                                      {creatingPortfolio ? 'Creating...' : 'Create Portfolio'}
                                    </button>

                                    {portfolioCreated && (
                                      <div className={`portfolio-creation-message ${portfolioCreated.success ? 'success' : 'error'}`}>
                                        {portfolioCreated.success ? (
                                          <>
                                            ✅ Portfolio "{portfolioCreated.portfolioName}" created successfully!
                                            <br />
                                            <small>ID: {portfolioCreated.portfolioId}</small>
                                          </>
                                        ) : (
                                          <>
                                            ❌ {portfolioCreated.error}
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <p className="no-data">No nearby simulations found</p>
                              )}
                            </div>
                          </>
                        ) : (
                          <p className="placeholder-text">Select a simulation to view optimal portfolio</p>
                        )}
                      </div>
                                        ) : activeTab === 'roles' ? (
                      <div className="role-analysis-content">
                        {metricsData?.data && selectedMetrics.length > 0 ? (
                          <>
                            <div className="percentile-control">
                              <label htmlFor="percentile-threshold">
                                Percentile Threshold: <strong>{percentileThreshold}%</strong>
                              </label>
                              <input
                                type="range"
                                id="percentile-threshold"
                                min="0"
                                max="50"
                                step="1"
                                value={percentileThreshold}
                                onChange={(e) => setPercentileThreshold(parseInt(e.target.value))}
                              />
                              <p className="control-hint">
                                Analyzing top and bottom {percentileThreshold}% of portfolios by each metric
                              </p>
                            </div>

                            <div className="role-analysis-table">
                              <h4>Asset Role Polarity Score</h4>
                              <p className="table-hint">
                                <span style={{color: 'green'}}>●</span> Positive = Driver (higher weight in better portfolios)
                                {' | '}
                                <span style={{color: 'yellow'}}>●</span> Near zero = Ballast (neutral impact)
                                {' | '}
                                <span style={{color: 'red'}}>●</span> Negative = Detractor (higher weight in worse portfolios)
                              </p>

                              {(() => {
                                const roleScores = calculateRoleAnalysis();
                                if (!roleScores) return <p className="no-data">No role analysis data available</p>;

                                let assets = Object.keys(roleScores);

                                // Apply sorting if configured
                                if (roleSortConfig.key) {
                                  assets = [...assets].sort((a, b) => {
                                    let aValue, bValue;

                                    if (roleSortConfig.key === 'asset') {
                                      aValue = a;
                                      bValue = b;
                                      return roleSortConfig.direction === 'asc'
                                        ? aValue.localeCompare(bValue)
                                        : bValue.localeCompare(aValue);
                                    } else {
                                      // Sorting by metric
                                      aValue = roleScores[a][roleSortConfig.key] || 0;
                                      bValue = roleScores[b][roleSortConfig.key] || 0;
                                      return roleSortConfig.direction === 'asc'
                                        ? aValue - bValue
                                        : bValue - aValue;
                                    }
                                  });
                                } else {
                                  // Default: sort alphabetically by asset
                                  assets = assets.sort();
                                }

                                const handleSort = (key) => {
                                  setRoleSortConfig(prev => ({
                                    key,
                                    direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
                                  }));
                                };

                                return (
                                  <table className="role-table">
                                    <thead>
                                      <tr>
                                        <th
                                          onClick={() => handleSort('asset')}
                                          style={{ cursor: 'pointer', userSelect: 'none' }}
                                        >
                                          Asset {roleSortConfig.key === 'asset' && (roleSortConfig.direction === 'asc' ? '▲' : '▼')}
                                        </th>
                                        {selectedMetrics.map(metric => (
                                          <th
                                            key={metric}
                                            onClick={() => handleSort(metric)}
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                          >
                                            {formatMetricName(metric)} {roleSortConfig.key === metric && (roleSortConfig.direction === 'asc' ? '▲' : '▼')}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {assets.map(asset => (
                                        <tr key={asset}>
                                          <td className="asset-name">{asset}</td>
                                          {selectedMetrics.map(metric => {
                                            const score = roleScores[asset][metric];
                                            const color = getRoleScoreColor(score);
                                            return (
                                              <td
                                                key={metric}
                                                className="role-score-cell"
                                                style={{
                                                  backgroundColor: color,
                                                  color: Math.abs(score) < 20 ? '#333' : '#fff',
                                                  fontWeight: 'bold'
                                                }}
                                              >
                                                {score.toFixed(2)}
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                );
                              })()}
                            </div>

                            {/* Weighted Role Analysis for Pinned Group */}
                            {pinnedPortfolios.length > 0 && (() => {
                              const blendedWeights = calculateBlendedPinnedPortfolio();
                              const roleScores = calculateRoleAnalysis();

                              if (!blendedWeights || !roleScores) return null;

                              return (
                                <div className="role-analysis-table" style={{ marginTop: '40px' }}>
                                  <h4>Weighted Role Impact for Pinned Group</h4>
                                  <p className="table-hint">
                                    Role scores weighted by asset allocation in blended pinned portfolio ({pinnedPortfolios.length} portfolios)
                                  </p>

                                  <table className="role-table">
                                    <thead>
                                      <tr>
                                        <th>Asset</th>
                                        <th>Avg Weight (%)</th>
                                        {selectedMetrics.map(metric => (
                                          <th key={metric}>
                                            {formatMetricName(metric)}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {Object.keys(roleScores).sort().map(asset => {
                                        const weight = blendedWeights[asset] || 0;
                                        return (
                                          <tr key={asset}>
                                            <td className="asset-name">{asset}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                              {weight.toFixed(2)}%
                                            </td>
                                            {selectedMetrics.map(metric => {
                                              const roleScore = roleScores[asset][metric];
                                              const weightedScore = roleScore * (weight / 100); // Multiply by weight as fraction
                                              const color = getRoleScoreColor(roleScore); // Keep original color scale
                                              return (
                                                <td
                                                  key={metric}
                                                  className="role-score-cell"
                                                  style={{
                                                    backgroundColor: color,
                                                    color: Math.abs(roleScore) < 20 ? '#333' : '#fff',
                                                    fontWeight: 'bold'
                                                  }}
                                                >
                                                  {weightedScore.toFixed(2)}
                                                </td>
                                              );
                                            })}
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                    <tfoot>
                                      <tr style={{ borderTop: '2px solid #333', fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                                        <td colSpan="2" style={{ textAlign: 'right', padding: '10px' }}>
                                          Portfolio Total Impact:
                                        </td>
                                        {selectedMetrics.map(metric => {
                                          // Calculate sum of weighted scores for this metric
                                          const totalImpact = Object.keys(roleScores).reduce((sum, asset) => {
                                            const weight = blendedWeights[asset] || 0;
                                            const roleScore = roleScores[asset][metric];
                                            const weightedScore = roleScore * (weight / 100);
                                            return sum + weightedScore;
                                          }, 0);

                                          const color = getRoleScoreColor(totalImpact);
                                          return (
                                            <td
                                              key={metric}
                                              style={{
                                                backgroundColor: color,
                                                color: Math.abs(totalImpact) < 20 ? '#333' : '#fff',
                                                fontWeight: 'bold',
                                                fontSize: '1.1em',
                                                textAlign: 'center',
                                                padding: '10px'
                                              }}
                                            >
                                              {totalImpact.toFixed(2)}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              );
                            })()}
                          </>
                        ) : (
                          <p className="placeholder-text">Load metrics data to view role analysis</p>
                        )}
                      </div>
                    ) : activeTab === 'selection' ? (
                      <div className="portfolio-selection-content">
                        {metricsData?.data ? (
                          <>
                            <div className="selection-info">
                              <h4>Portfolio Selection Filters</h4>
                              <p className="control-hint">
                                {filteredSimulations.length} / {metricsData.data.length} portfolios match criteria
                                {pinnedPortfolios.length > 0 && (
                                  <> | <span style={{color: '#E74C3C', fontWeight: 'bold'}}>{pinnedPortfolios.length} pinned</span></>
                                )}
                              </p>
                              <button
                                className={`pin-group-btn ${pinnedPortfolios.length > 0 ? 'pinned' : ''}`}
                                onClick={togglePinGroup}
                                disabled={filteredSimulations.length === 0 && pinnedPortfolios.length === 0}
                              >
                                {pinnedPortfolios.length > 0 ? '🔓 Отпустить группу' : '🔒 Зафиксировать группу'}
                              </button>
                            </div>

                            {/* Metric Range Filters */}
                            <div className="filter-section">
                              <h4>Filter by Metrics</h4>
                              {selectedMetrics.map(metric => {
                                const range = metricRanges[metric];
                                if (!range) return null;

                                const values = metricsData.data.map(d => d[metric]);
                                const absMin = Math.min(...values);
                                const absMax = Math.max(...values);
                                const step = (absMax - absMin) / 200;

                                return (
                                  <div key={metric} className="range-filter">
                                    <label>{formatMetricName(metric)}</label>
                                    <div className="range-values">
                                      <span>{range.min.toFixed(4)}</span>
                                      <span>{range.max.toFixed(4)}</span>
                                    </div>
                                    <div className="dual-slider-container">
                                      <input
                                        type="range"
                                        min={absMin}
                                        max={absMax}
                                        step={step}
                                        value={range.min}
                                        onChange={(e) => {
                                          const newMin = parseFloat(e.target.value);
                                          setMetricRanges(prev => ({
                                            ...prev,
                                            [metric]: { ...prev[metric], min: Math.min(newMin, prev[metric].max) }
                                          }));
                                        }}
                                        className="range-slider range-slider-min"
                                      />
                                      <input
                                        type="range"
                                        min={absMin}
                                        max={absMax}
                                        step={step}
                                        value={range.max}
                                        onChange={(e) => {
                                          const newMax = parseFloat(e.target.value);
                                          setMetricRanges(prev => ({
                                            ...prev,
                                            [metric]: { ...prev[metric], max: Math.max(newMax, prev[metric].min) }
                                          }));
                                        }}
                                        className="range-slider range-slider-max"
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Asset Range Filters */}
                            <div className="filter-section">
                              <h4>Filter by Asset Allocation (%)</h4>
                              {Object.keys(assetRanges).sort().map(asset => {
                                const range = assetRanges[asset];

                                return (
                                  <div key={asset} className="range-filter">
                                    <label>{asset}</label>
                                    <div className="range-values">
                                      <span>{range.min.toFixed(1)}%</span>
                                      <span>{range.max.toFixed(1)}%</span>
                                    </div>
                                    <div className="dual-slider-container">
                                      <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        step="0.5"
                                        value={range.min}
                                        onChange={(e) => {
                                          const newMin = parseFloat(e.target.value);
                                          setAssetRanges(prev => ({
                                            ...prev,
                                            [asset]: { ...prev[asset], min: Math.min(newMin, prev[asset].max) }
                                          }));
                                        }}
                                        className="range-slider range-slider-min"
                                      />
                                      <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        step="0.5"
                                        value={range.max}
                                        onChange={(e) => {
                                          const newMax = parseFloat(e.target.value);
                                          setAssetRanges(prev => ({
                                            ...prev,
                                            [asset]: { ...prev[asset], max: Math.max(newMax, prev[asset].min) }
                                          }));
                                        }}
                                        className="range-slider range-slider-max"
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Reset Filters Button */}
                            <div className="filter-actions">
                              <button
                                className="reset-filters-btn"
                                onClick={() => {
                                  // Reset metric ranges to full range
                                  const resetMetricRanges = {};
                                  selectedMetrics.forEach(metric => {
                                    const values = metricsData.data.map(d => d[metric]);
                                    resetMetricRanges[metric] = {
                                      min: Math.min(...values),
                                      max: Math.max(...values)
                                    };
                                  });
                                  setMetricRanges(resetMetricRanges);

                                  // Reset asset ranges to 0-100%
                                  const resetAssetRanges = {};
                                  Object.keys(assetRanges).forEach(asset => {
                                    resetAssetRanges[asset] = { min: 0, max: 100 };
                                  });
                                  setAssetRanges(resetAssetRanges);
                                }}
                              >
                                Reset All Filters
                              </button>
                            </div>
                          </>
                        ) : (
                          <p className="placeholder-text">Load metrics data to use portfolio selection</p>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ParetoAnalysis;
